import { HttpErrorResponse } from '@angular/common/http';
import { TimeoutError as RxTimeoutError } from 'rxjs';
import { ApiError, NetworkError } from '../../models';

/**
 * Single normalisation point for everything that can come out of `HttpClient`
 * as an error. Used by `httpErrorInterceptor`. Centralising the mapping here
 * keeps the interceptor focused on flow control and lets domain code rely on
 * a closed set of error types (`ApiError` | `NetworkError`).
 */
export function normaliseHttpError(error: unknown): ApiError | NetworkError {
  if (error instanceof ApiError || error instanceof NetworkError) {
    return error;
  }

  if (error instanceof RxTimeoutError) {
    return new ApiError('Request timeout. The operation may still be processing.', 408);
  }

  if (error instanceof HttpErrorResponse) {
    // Browser-level / client-side failures (CORS, DNS, offline) carry
    // `status === 0` — no HTTP response ever arrived. Older browsers also
    // surfaced these as an `ErrorEvent` on `error.error`; modern Chromium
    // uses a `ProgressEvent`. Either signal means "no server response",
    // which is a `NetworkError`, not a parseable API response.
    if (error.status === 0 || error.error instanceof ErrorEvent) {
      return new NetworkError();
    }

    const { message, code, details } = parseErrorBody(error.error);
    // When we lifted a wire code but no human message came back, DON'T
    // fall back to `error.message` — that's the framework's
    // `"Http failure response for <url>: 403 Forbidden"` string, which
    // is worse than nothing. Passing empty lets
    // `getUserFriendlyErrorMessage` render the localised
    // `errors.forbidden_403` copy instead.
    const finalMessage = message ?? (code ? '' : error.message);
    // `error.error` is the parsed body — carried through so denial fields
    // (`area`, `mode`, `hint`) survive normalisation. See `ApiError.body`.
    return new ApiError(finalMessage, error.status, code, details, error.error);
  }

  const message = error instanceof Error ? error.message : 'An unknown error occurred';
  return new ApiError(message, 0);
}

/**
 * Backend error payloads come in a handful of shapes:
 *   - `{ message: "..." }`
 *   - `{ error: "..." }` (legacy plain-text OR a snake_case wire code)
 *   - `{ error: { message: "...", code: "..." } }` (Icicle/intake)
 *   - `{ message: "...", code: "..." }` (newer)
 *   - `{ error: "<sentence>", code: "feature_not_entitled", feature_id: ... }`
 *     (entitlement / capability gate — see `models/access-error.model.ts`)
 *
 * Accept any of them; prefer the most specific.
 *
 * Wire codes on `record['error']` (snake_case single word, e.g.
 * `feature_not_entitled`, `no_active_subscription`) are lifted to
 * `code`, **not** `message` — otherwise `getUserFriendlyErrorMessage`
 * would paint the raw key onto the toast. Prose messages
 * (`"Something bad happened."`) still fall through as `message`.
 *
 * That `isWireCode` sniff is now legacy cover rather than the live path.
 * Since 2026-09-08 the gates put prose in `error` and the code in `code`,
 * which this function already reads first — so a denial now yields both a
 * real `message` and a real `code`, and `getUserFriendlyErrorMessage`'s 403
 * branch surfaces the backend's sentence instead of the generic
 * `errors.forbidden_403` copy. Kept because it costs nothing and still
 * covers any endpoint that never moved.
 */
function parseErrorBody(body: unknown): { message?: string; code?: string; details?: unknown } {
  if (!body || typeof body !== 'object') return {};
  const record = body as Record<string, unknown>;

  const nested =
    record['error'] && typeof record['error'] === 'object'
      ? (record['error'] as Record<string, unknown>)
      : undefined;

  const rawError = pickString(record['error']);
  const errorIsWireCode = !!rawError && isWireCode(rawError);

  const message =
    pickString(nested?.['message']) ??
    pickString(record['message']) ??
    (errorIsWireCode ? undefined : rawError);

  const code =
    pickString(nested?.['code']) ??
    pickString(record['code']) ??
    (errorIsWireCode ? rawError : undefined);

  // `details` is the structured sibling used by codes like `activation_blocked`
  // to carry compliance findings. Kept as `unknown` — per-code narrowing lives
  // with the consumer. Only lifted when it's actually an object payload.
  const rawDetails = nested?.['details'] ?? record['details'];
  const details =
    rawDetails && typeof rawDetails === 'object' ? (rawDetails as unknown) : undefined;

  return { message, code, details };
}

/** True when `s` looks like a machine-readable wire code, not prose —
 *  lower-case letters, digits, and underscores only, no whitespace.
 *  Matches every value emitted by the BE entitlement gate today
 *  (`feature_not_entitled`, `no_active_subscription`, ...) and their
 *  future siblings, plus non-canonical variants (`feat_not_entitled`)
 *  that the FE enum wouldn't recognise. */
function isWireCode(s: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(s);
}

function pickString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
